import { Component, Inject, inject } from '@angular/core';
import { AbstractStepDTO, SmsStepDTO } from '@tj/tj-dmp-lib-api';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { AbstractStepConfigComponent } from '../abstract-step-config/abstract-step-config.component';
import { ON_CANCEL_TOKEN, ON_SAVE_TOKEN, PARTNER_TOKEN, READ_ONLY_MODE_TOKEN, STEP_TOKEN, TENANT_TOKEN } from '../injection-tokens';
import { StepDescriptionService } from '../step-description.service';

@Component({
    selector: 'dmp-sms-step-config',
    templateUrl: './sms-step-config.component.html',
    standalone: false
})
export class SmsStepConfigComponent extends AbstractStepConfigComponent {
  private readonly stepDescriptionService = inject(StepDescriptionService);

  smsForm: FormGroup;
  availableSmsTemplates: { label: string; value: string }[] = [];

  get smsStep(): SmsStepDTO {
    return this.step as SmsStepDTO;
  }

  get smsTemplateId() { 
    return this.smsForm.get('smsTemplateId'); 
  }

  constructor(
    @Inject(STEP_TOKEN) step: AbstractStepDTO,
    @Inject(TENANT_TOKEN) tenant: string,
    @Inject(PARTNER_TOKEN) partner: string,
    @Inject(READ_ONLY_MODE_TOKEN) readOnlyMode: string,
    @Inject(ON_SAVE_TOKEN) onSaveCallback: () => void,
    @Inject(ON_CANCEL_TOKEN) onCancelCallback: () => void,
    private formBuilder: FormBuilder
  ) {
    super(step, tenant, partner, readOnlyMode, onSaveCallback, onCancelCallback);
  }

  protected initializeComponent(): void {
    this.createForm();
    this.loadSmsTemplates();
  }

  protected updateStepFromForm(): void {
    if (!this.smsStep || !this.smsForm.valid) return;

    const smsTemplateId  = this.smsTemplateId.value;

    this.smsStep.description = this.generateReadableDescription(smsTemplateId);;
    this.smsStep.smsTemplateId = smsTemplateId;
  }

  protected isConfigurationValid(): boolean {
    return this.smsForm.valid;
  }

  private createForm(): void {
    this.smsForm = this.formBuilder.group({
      smsTemplateId: this.createFormControl('', [Validators.required])
    });

    this.smsForm.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      if (!this.isInitializing) {
        this.markAsChanged();
      }
    });
  }

  private loadSmsTemplates(): void {
    // TODO: Remplacer par l'appel API réel quand le service sera disponible
    // Pour l'instant, templates SMS factices
    this.availableSmsTemplates = [
      { label: 'SMS de bienvenue', value: 'welcome-sms-001' },
      { label: 'SMS de confirmation', value: 'confirmation-sms-002' },
      { label: 'SMS de rappel', value: 'reminder-sms-003' },
      { label: 'SMS promotionnel', value: 'promo-sms-004' },
      { label: 'SMS de relance', value: 'followup-sms-005' },
      { label: 'SMS d\'abandon panier', value: 'cart-abandon-sms-006' },
      { label: 'SMS de remerciement', value: 'thanks-sms-007' },
      { label: 'SMS d\'anniversaire', value: 'birthday-sms-008' }
    ].sort((a, b) => a.label.localeCompare(b.label));

    this.initializeForm();
  }

  private initializeForm(): void {
    if (!this.smsStep.smsTemplateId) return;

    this.isInitializing = true;

    this.smsForm.patchValue({
      smsTemplateId: this.smsStep.smsTemplateId
    });

    this.isInitializing = false;
  }

  private generateReadableDescription(templateId: string): string {
    const template = this.availableSmsTemplates.find(sms => sms.value === templateId);
    return this.stepDescriptionService.sms(template?.label || templateId);
  }
}